import { formulaLeaves } from "./formula.js";
import { providerFor } from "./market.js";
import { isConditionGroup, operandOffset } from "./spec.js";
import { BACKTEST_FILE_SUFFIX, GZIP_SUFFIX, STRATEGY_FILE_SUFFIX, STRAT_APP_VERSION, STRAT_FORMAT, STRAT_FORMAT_VERSION, STRAT_FORMAT_VERSION_BASE, } from "./version.js";
/**
 * The lowest format version that can express this strategy.
 *
 * Version 2 only when the strategy looks back (`offset`), reads a slower chart
 * (`timeframe`) or calculates (`formula`). Everything else stays version 1, so
 * an older desktop build still opens it — and refuses, with "update to open
 * it", exactly the files it would otherwise have run wrong.
 */
export function formatVersionFor(spec) {
    const looksBack = (node) => {
        if (!node || typeof node !== "object")
            return false;
        if (isConditionGroup(node))
            return (node.children ?? []).some(looksBack);
        return [node.left, node.right].some((side) => !!side && operandOffset(side) > 0);
    };
    const indicators = Array.isArray(spec?.indicators) ? spec.indicators : [];
    const needsTwo = indicators.some((d) => d?.kind === "formula" ||
        d?.timeframe !== undefined ||
        (d?.formula !== undefined && formulaLeaves(d.formula).some((l) => operandOffset(l) > 0))) ||
        looksBack(spec?.entry) ||
        looksBack(spec?.exit);
    return needsTwo ? STRAT_FORMAT_VERSION : STRAT_FORMAT_VERSION_BASE;
}
function versionFor(specs) {
    return specs.reduce((v, spec) => Math.max(v, formatVersionFor(spec)), STRAT_FORMAT_VERSION_BASE);
}
/**
 * The header every document carries.
 *
 * `now` is an argument rather than a `new Date()` call, and that is the whole
 * reason this package can sit behind the purity fence. An export that stamps
 * itself with the ambient clock is not reproducible: the same strategy exported
 * twice produces two different files, so no test can assert on the bytes and
 * `strat export --at` has nowhere to inject a fixed time.
 */
function header(kind, now, specs) {
    return {
        format: STRAT_FORMAT,
        formatVersion: versionFor(specs),
        kind,
        exportedAt: now,
        generator: { name: "strat", version: STRAT_APP_VERSION },
    };
}
export function strategyDocument(strategy, now, options = {}) {
    return {
        ...header("strategy", now, [strategy]),
        strategy,
        ...(options.settings ? { settings: options.settings } : {}),
        ...(options.profile ? { profile: options.profile } : {}),
        ...(options.origin ? { origin: options.origin } : {}),
    };
}
export function runDocument(run, now) {
    return { ...header("run", now, [run.strategy]), run };
}
export function bundleDocument(strategies, runs, now) {
    return {
        ...header("bundle", now, [...strategies.map((s) => s.strategy), ...runs.map((r) => r.strategy)]),
        strategies,
        runs,
    };
}
/**
 * Where a run's bars came from, from whatever the run says: its own
 * `dataSource` when the file carried one, otherwise the provider in its params
 * or the one its market type implies.
 */
export function runDataSource(run) {
    if (run.dataSource)
        return run.dataSource;
    return {
        provider: run.params?.provider || providerFor(run.marketType),
        symbol: run.symbol,
        marketType: run.marketType,
        timeframe: run.timeframe,
        from: run.params?.from ?? null,
        to: run.params?.to ?? null,
        warmupBars: run.result?.warmupBarsUsed ?? run.params?.warmupBars ?? 0,
        barsTested: run.result?.barsTested ?? 0,
    };
}
/** Turns a stored run into its portable form. */
export function toPortableRun(detail) {
    const run = {
        sourceId: detail.id,
        strategyName: detail.strategyName,
        symbol: detail.symbol,
        timeframe: detail.timeframe,
        marketType: detail.marketType,
        leverage: detail.leverage,
        batchLabel: detail.batchLabel,
        params: detail.params,
        ranAt: detail.createdAt,
        strategy: detail.spec,
        result: detail.result,
    };
    return { ...run, dataSource: runDataSource(run) };
}
/**
 * The suffix a document is written with: `.backtest` when it holds a finished
 * run, `.strategy` when it holds rules only — plus `.gz` when compressed.
 */
export function fileSuffixFor(document, options = {}) {
    const hasRuns = document.kind === "run" || (document.kind === "bundle" && (document.runs?.length ?? 0) > 0);
    return `${hasRuns ? BACKTEST_FILE_SUFFIX : STRATEGY_FILE_SUFFIX}${options.compressed ? GZIP_SUFFIX : ""}`;
}
/**
 * Builds a filename for a document. Lowercase, hyphenated and ASCII-only, so
 * the same file survives a round trip through any filesystem.
 */
export function portableFilename(parts, suffix = STRATEGY_FILE_SUFFIX) {
    const slug = parts
        .filter((p) => !!p)
        .map((p) => p
        .normalize("NFKD")
        .replace(/[^\w\s-]+/g, "")
        .trim()
        .replace(/[\s_]+/g, "-")
        .toLowerCase())
        .filter(Boolean)
        .join("-")
        .replace(/-{2,}/g, "-")
        .replace(/^-|-$/g, "");
    return `${slug || "export"}${suffix}`;
}
//# sourceMappingURL=write.js.map